import mockAuthService from './mockAuthService';
import cognitoAuthService from './cognitoAuthService';

// Check if we should use mock auth
const USE_MOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

// Select the appropriate auth service
const authServiceProvider = USE_MOCK_AUTH ? mockAuthService : cognitoAuthService;

if (USE_MOCK_AUTH) {
  console.log('🔐 Using Mock Cognito Authentication');
} else {
  console.log('🔐 Using Direct AWS Cognito Authentication (no Amplify)');
}

export interface SignUpParams {
  username: string;
  password: string;
  email: string;
  givenName: string;
  familyName: string;
  gender?: string;
  usta_level?: string;
}

export interface SignInParams {
  username: string;
  password: string;
}

export interface SignInResult {
  success: boolean;
  error?: string;
  requiresPasswordChange?: boolean;
  requiresMFA?: boolean;
  challengeData?: {
    Session?: string;
    ChallengeName?: string;
    ChallengeParameters?: Record<string, string>;
    Username?: string;
  };
  result?: {
    isSignedIn: boolean;
  };
}

export interface ConfirmSignUpParams {
  username: string;
  code: string;
}

export const authService = {
  // Sign up a new user
  signUp: async ({ username, password, email, givenName, familyName, gender, usta_level }: SignUpParams) => {
    return authServiceProvider.signUp({ username, password, email, givenName, familyName, gender, usta_level });
  },

  // Confirm sign up with verification code
  confirmSignUp: async ({ username, code }: ConfirmSignUpParams) => {
    return authServiceProvider.confirmSignUp({ username, code });
  },

  // Resend confirmation code
  resendConfirmationCode: async (username: string) => {
    return authServiceProvider.resendConfirmationCode(username);
  },

  // Sign in
  signIn: async ({ username, password }: SignInParams): Promise<SignInResult> => {
    return authServiceProvider.signIn({ username, password });
  },

  // Complete new password challenge (for first-time login)
  completeNewPassword: async (newPassword: string, session?: string, username?: string) => {
    if (USE_MOCK_AUTH) {
      return authServiceProvider.completeNewPassword(newPassword, session);
    }
    // For direct Cognito, session is required
    if (!session) {
      return { success: false, error: 'Session required for password change' };
    }
    return authServiceProvider.completeNewPassword(newPassword, session, username);
  },

  // Sign out
  signOut: async () => {
    return authServiceProvider.signOut();
  },

  // Get current authenticated user
  getCurrentUser: async () => {
    return authServiceProvider.getCurrentUser();
  },

  // Get current session (includes tokens)
  getCurrentSession: async () => {
    return authServiceProvider.getCurrentSession();
  },

  // Get ID token for API calls
  getIdToken: async (): Promise<string | null> => {
    return authServiceProvider.getIdToken();
  },

  // Respond to MFA challenge
  respondToMFAChallenge: async (code: string, session: string, username?: string) => {
    if (USE_MOCK_AUTH) {
      // Mock auth doesn't support MFA
      return { success: false, error: 'MFA not supported in mock mode' };
    }
    return authServiceProvider.respondToMFAChallenge(code, session, username);
  },
};

export default authService;
